import { AlertCircle, ClipboardList, FileText, Loader2 } from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { type VisitListStatus, visitListStatus } from "./visit-list-model";

interface Props {
  status: string | null;
  // The visit is being deleted from this row.
  deleting?: boolean;
  className?: string;
}

const PILL =
  "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold leading-5";

// "Pre-chart · draft": prepared before the visit, not recorded yet. A
// dashed outline, as a draft that is not finished.
export const PreChartBadge = ({
  className,
}: {
  className?: string;
}): JSX.Element => {
  const { t } = useTranslation();
  return (
    <span
      className={cn(
        PILL,
        "border border-dashed border-v3-border-strong bg-v3-panel py-px text-v3-text-2",
        className,
      )}
      data-status="preChart"
      data-testid="visit-status"
    >
      <ClipboardList aria-hidden className="size-3.5" />
      {t("preChart.badge")}
    </span>
  );
};

// "Report": a dictated report (radiology dictation), saved as a visit.
export const ReportBadge = ({
  className,
}: {
  className?: string;
}): JSX.Element => {
  const { t } = useTranslation();
  return (
    <span
      className={cn(PILL, "bg-v3-brand-tint text-v3-brand-strong", className)}
      data-status="report"
      data-testid="visit-status"
    >
      <FileText aria-hidden className="size-3.5" />
      {t("dictationReport.badge")}
    </span>
  );
};

// Processing, Writing note, Ready and Error, in the pill style of the
// workspace's recording count. Ready stays quiet (a dot); work in progress
// and errors stand out.
export const VisitStatusBadge = ({
  status,
  deleting = false,
  className,
}: Props): JSX.Element | null => {
  const { t } = useTranslation();
  const value: VisitListStatus | "deleting" = deleting
    ? "deleting"
    : visitListStatus(status);

  switch (value) {
    case "deleting":
    case "processing":
    case "generating":
      return (
        <span
          className={cn(
            PILL,
            "bg-v3-brand-tint text-v3-brand-strong",
            className,
          )}
          data-status={value}
          data-testid="visit-status"
        >
          <Loader2 aria-hidden className="size-3.5 animate-spin" />
          {t(`visits.home.status.${value}`)}
        </span>
      );
    case "ready":
      return (
        <span
          className={cn(PILL, "px-0 font-medium text-v3-text-3", className)}
          data-status={value}
          data-testid="visit-status"
        >
          <span aria-hidden className="size-2 rounded-full bg-success" />
          {t("visit.statusFile.ready")}
        </span>
      );
    case "preChart":
      return <PreChartBadge className={className} />;
    case "report":
      return <ReportBadge className={className} />;
    case "error":
      return (
        <span
          className={cn(PILL, "bg-error/10 text-error", className)}
          data-status={value}
          data-testid="visit-status"
        >
          <AlertCircle aria-hidden className="size-3.5" />
          {t("visit.statusFile.error")}
        </span>
      );
    default:
      return null;
  }
};
